import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/SectionHeader";
import { api, IMG } from "@/lib/api";
import { toast } from "sonner";


export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [available, setAvailable] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    let mounted = true;
    api.get("/contact/status").then(({ data }) => {
      if (mounted) setAvailable(data.available === true);
    }).catch(() => {}).finally(() => { if (mounted) setChecking(false); });
    return () => { mounted = false; };
  }, []);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !available) return;
    setBusy(true);
    setStatus("");
    try {
      await api.post("/contact", form);
      const ok = "Thank you. Your message has been sent to Wolfdog Village.";
      setStatus(ok);
      toast.success(ok);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
    } catch (error) {
      const err = error.response?.status === 429 ? "Please wait a few minutes before trying again. Your message is still here." : "Your message could not be sent right now. Your message is still here so you can try again later.";
      setStatus(err);
      toast.error(err);
    } finally { setBusy(false); }
  };

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title="Contact Wolfdog Village"
        description="Questions, suggestions, or need a little help?"
        image={IMG.hero}
      />
      <section className="container-x py-16">
        <form onSubmit={submit} data-testid="contact-form" className="max-w-3xl rounded-lg border border-border bg-card p-8">
          <p role="status" className="mb-6 text-sm text-muted-foreground">{checking ? "Checking contact form availability…" : available ? "Use this form to contact Wolfdog Village. Please do not include passwords or sensitive personal information." : "Our contact form is being set up and is not accepting messages yet."}</p>
          <fieldset disabled={busy || !available}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input required maxLength={100} value={form.name} onChange={setF("name")} data-testid="contact-name" className="input" /></Field>
            <Field label="Email"><input required type="email" maxLength={254} value={form.email} onChange={setF("email")} data-testid="contact-email" className="input" /></Field>
          </div>
          <Field label="What's your message about?" className="mt-4"><select required value={form.subject} onChange={setF("subject")} data-testid="contact-subject" className="input"><option value="" disabled>Choose a topic</option>{["General question","Free membership or account help","Am I Ready For a Wolfdog? or Breeder Evaluation app","Learning Center or educational resources","Live classes and certificates","Breeder directory question","Report an outdated or broken link","Other"].map(t=><option key={t}>{t}</option>)}</select></Field>
          <Field label="Message" className="mt-4"><textarea required maxLength={5000} rows={6} value={form.message} onChange={setF("message")} data-testid="contact-message" className="input resize-none" /></Field>
          <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
            <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={setF("website")} /></label>
          </div>
          <button type="submit" disabled={busy || !available} aria-disabled={busy || !available} data-testid="contact-submit" className="btn-primary mt-6">{busy ? "Sending…" : "Send message"}</button>
          </fieldset>
          <p role="status" aria-live="polite" data-testid="contact-status" className="mt-4 text-sm text-muted-foreground">{status}</p>
        </form>
        <aside className="max-w-3xl mt-10"><h2 className="font-display text-2xl">A note about urgent matters</h2><p className="mt-4 text-sm text-muted-foreground">This contact form is for website and educational inquiries. It isn't monitored as an emergency service. For urgent animal-health or public-safety concerns, contact an appropriate local veterinary, emergency, or public-health service.</p></aside>
      </section>


      <style>{`.input{width:100%;padding:.75rem 1rem;font-size:.875rem;border-radius:.375rem;border:1px solid hsl(var(--border));background:hsl(var(--background));color:hsl(var(--foreground));} .input:focus{outline:none;box-shadow:0 0 0 2px hsl(var(--primary));}`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <span className="eyebrow">{label}</span>
      <span className="block mt-2">{children}</span>
    </label>
  );
}
